import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Mail, 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  ArrowLeft,
  UserPlus,
  CheckCircle,
  Shield,
  Sparkles,
  Zap,
  Crown,
  Star,
  ArrowRight
} from 'lucide-react';

const ModernRegisterPage = ({ onRegister, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('İsim gerekli');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Email gerekli');
      return false;
    }
    if (formData.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return false;
    }
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const response = await axios.post('/api/auth/register', {
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      
      setEmailSent(true);
      toast.success('Kayıt başarılı! Email doğrulama linki gönderildi.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Kayıt hatası');
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      name: 'Google Giriş',
      icon: Mail,
      color: 'from-amber-500 to-orange-500',
      features: ['100 mesaj/gün', '5 görsel/gün', '1 video/gün', 'Temel AI modelleri', 'Kalıcı veri saklama'],
      price: 'Ücretsiz'
    },
    {
      name: 'Premium Basic',
      icon: Zap,
      color: 'from-orange-500 to-red-500',
      features: ['500 mesaj/gün', '10 görsel/gün', '5 video/gün', 'Premium modeller', 'Öncelikli destek'],
      price: '₺19/ay',
      originalPrice: '₺29/ay',
      discount: '50%'
    },
    {
      name: 'Premium Pro',
      icon: Crown,
      color: 'from-amber-600 to-orange-600',
      features: ['Sınırsız mesaj', '100 görsel/gün', '50 video/gün', 'Tüm AI modelleri', 'Gelişmiş özellikler'],
      price: '₺49/ay',
      originalPrice: '₺99/ay',
      discount: '50%'
    },
    {
      name: 'Enterprise',
      icon: Star,
      color: 'from-orange-600 to-red-600',
      features: ['Her şey sınırsız', 'Özel AI modelleri', 'API erişimi', '7/24 destek', 'Özel entegrasyonlar'],
      price: '₺199/ay',
      originalPrice: '₺399/ay',
      discount: '50%'
    }
  ];

  if (emailSent) {
    return (
      <div className="h-screen gokturk-bg flex items-center justify-center p-4">
        <motion.div 
          className="relative z-20 w-full max-w-md"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
            <motion.div 
              className="text-6xl mb-4"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              📧
            </motion.div>
            <h2 className="text-2xl font-bold text-white mb-4">🏛️ E-posta Doğrulama</h2>
            <p className="text-amber-200 mb-6">
              {formData.email} adresine doğrulama linki gönderildi. 
              Lütfen e-posta kutunuzu kontrol edin ve linke tıklayarak hesabınızı doğrulayın.
            </p>
            <button
              onClick={onSwitchToLogin}
              className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-amber-700 hover:to-orange-700 transition-all duration-200 flex items-center space-x-2 mx-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Giriş Sayfasına Dön</span>
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-screen gokturk-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Göktürk Symbols Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 left-10 text-6xl opacity-10 animate-pulse">🏛️</div>
        <div className="absolute top-20 right-20 text-5xl opacity-10 animate-pulse delay-1000">⚔️</div>
        <div className="absolute bottom-20 left-20 text-4xl opacity-10 animate-pulse delay-2000">🛡️</div>
        <div className="absolute bottom-10 right-10 text-5xl opacity-10 animate-pulse delay-3000">👑</div>
        <div className="absolute top-1/2 left-1/4 text-3xl opacity-10 animate-pulse delay-1000">🏛️</div>
        <div className="absolute top-1/3 right-1/4 text-4xl opacity-10 animate-pulse delay-2000">⚔️</div>
      </div>

      {/* GÖKTÜRKGPT Header Animation */}
      <motion.div 
        className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        <motion.h1 
          className="text-[9px] sm:text-[10px] md:text-xs lg:text-sm xl:text-base font-bold text-white text-center py-0.5 px-1 leading-none"
          animate={{ 
            textShadow: [
              "0 0 20px rgba(255,255,255,0.5)",
              "0 0 40px rgba(255,255,255,0.8)",
              "0 0 20px rgba(255,255,255,0.5)"
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          GÖKTÜRKGPT - TÜRK YAPAY ZEKASI
        </motion.h1>
      </motion.div>

      <div className="relative z-20 w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Register Form */}
        <motion.div 
          className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">🏛️ GöktürkGPT'e Katılın</h2>
            <p className="text-amber-200">Türk Yapay Zekası ile Sohbet Edin</p>
            <p className="text-amber-300 text-sm mt-2">Türk tarihi ve kültürü ile harmanlanmış yapay zeka deneyimi</p>
          </div>

          <motion.form
            onSubmit={handleRegister}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Ad Soyad
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50 w-4 h-4" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Adınız Soyadınız"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Email Adresi
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50 w-4 h-4" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ornek@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Şifre
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50 w-4 h-4" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="En az 6 karakter"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Şifre Tekrar
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50 w-4 h-4" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Şifrenizi tekrar girin"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-amber-700 hover:to-orange-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Kayıt Oluşturuluyor...</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  <span>🏛️ GöktürkGPT'e Katıl</span>
                </>
              )}
            </button>
          </motion.form>

          <div className="mt-6 text-center">
            <p className="text-amber-200">
              Zaten hesabınız var mı?{' '}
              <button
                onClick={onSwitchToLogin}
                className="text-amber-400 hover:text-amber-300 font-semibold transition-colors duration-200 flex items-center space-x-1 mx-auto"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Giriş Yap</span>
              </button>
            </p>
          </div>
        </motion.div>

        {/* Plans Overview */}
        <motion.div 
          className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/10 p-6"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-white mb-2">🏛️ GöktürkGPT Planları</h3>
            <p className="text-amber-200">Size uygun planı seçin</p>
            <div className="mt-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold inline-block">
              🎉 ÖZEL FIRSAT! Tüm Premium Planlarda %50 İndirim 🎉
            </div>
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`bg-gradient-to-r ${plan.color} rounded-lg p-4 text-white`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <plan.icon className="w-6 h-6" />
                    <div>
                      <h4 className="font-semibold text-lg">{plan.name}</h4>
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold">{plan.price}</span>
                        {plan.originalPrice && (
                          <>
                            <span className="text-sm line-through opacity-70">{plan.originalPrice}</span>
                            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">{plan.discount}</span>
                          </>
                        )}
                        {!plan.originalPrice && plan.price === 'Ücretsiz' && (
                          <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">✓ Ücretsiz</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {plan.price !== 'Ücretsiz' && (
                    <button className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-full text-sm font-semibold transition-all duration-200">
                      Seç
                    </button>
                  )}
                </div>
                
                <div className="space-y-1">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-300" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <p className="text-amber-200 text-sm">
              <Shield className="w-4 h-4 inline mr-1" />
              🏛️ Güvenli ve şifrelenmiş bağlantı
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ModernRegisterPage;
